import { Controller } from '@hotwired/stimulus';
import Rails from "@rails/ujs";

export default class extends Controller {
  static targets = [ 'checkbox', 'query', 'clearButton' ];

  connect(){
    this.boundUpdateURL = this.updateURL.bind(this);
    this.element.addEventListener('turbo:submit-end', this.boundUpdateURL);
    this.boundStartLoading = this.startLoading.bind(this);
    this.boundStopLoading = this.stopLoading.bind(this);
    this.element.addEventListener('turbo:submit-start', this.boundStartLoading);
    this.element.addEventListener('turbo:submit-end', this.boundStopLoading);
    this.boundClearPendingResults = this.clearPendingResults.bind(this);
    document.addEventListener('turbo:fetch-request-error', this.boundClearPendingResults);
    this.boundScrollResultsToTop = this.scrollResultsToTop.bind(this);
    document.addEventListener('turbo:before-stream-render', this.boundScrollResultsToTop);
    this.processFilterCountMutation(this.element);
  }

  disconnect() {
    this.element.removeEventListener('turbo:submit-end', this.boundUpdateURL);
    this.element.removeEventListener('turbo:submit-start', this.boundStartLoading);
    this.element.removeEventListener('turbo:submit-end', this.boundStopLoading);
    document.removeEventListener('turbo:fetch-request-error', this.boundClearPendingResults);
    document.removeEventListener('turbo:before-stream-render', this.boundScrollResultsToTop);
  }

  submit() {
    // Filter changes and results-page query submits come through here. The cold
    // modal uses this only for exploratory filter counts (embedded=1); an
    // explicit cold query navigates directly to results instead.
    this.markResultsPending();
    Rails.fire(this.element, 'submit');
  }

  // The query is not searched on each keystroke. Enter calls this; Show
  // results does too when the box has uncommitted text.
  submitQuery() {
    this.prepareQuerySubmit();
    this.submit();
  }

  prepareQuerySubmit() {
    this.resetOrderForQueryChange();
    this.rememberQuery();
  }

  queryIsDirty() {
    if (!this.hasQueryTarget) return false;

    return this.queryTarget.value !== this.committedQuery;
  }

  rememberQuery() {
    this.committedQuery = this.hasQueryTarget ? this.queryTarget.value : '';
  }

  startLoading() {
    this.element.classList.add('is-search-updating');
    this.element.setAttribute('aria-busy', 'true');
  }

  stopLoading() {
    this.element.classList.remove('is-search-updating');
    this.element.removeAttribute('aria-busy');
  }

  markResultsPending() {
    const results = document.getElementById('search_results');
    results?.classList.add('is-search-pending');
    results?.setAttribute('aria-busy', 'true');
  }

  clearPendingResults() {
    const results = document.getElementById('search_results');
    results?.classList.remove('is-search-pending');
    results?.removeAttribute('aria-busy');
    this.stopLoading();
    if (this.isCold) this.modalController?.endColdNavigation();
  }

  // The cold modal (off the results page) has no #search_results region.
  get isCold() {
    return !document.getElementById('search_results');
  }

  clearQuery() {
    this.queryTarget.value = '';
    this.toggleClearButton();
    this.resetOrderForQueryChange();
    this.rememberQuery();
    // Keep this synchronous with the clear-button tap so Mobile Safari opens
    // the keyboard for the replacement query. preventScroll also avoids the
    // focused field nudging an open, fixed search sheet.
    this.queryTarget.focus({ preventScroll: true });
    this.submit();
  }

  // Changing the search term should return to relevance ranking. An earlier
  // Most Recent / Liked / Viewed choice stays checked in the form radios and
  // would otherwise keep overriding the server's query-aware default.
  // Leave radios unchecked (so order isn't submitted) but mark the effective
  // default with .is-default — turbo streams don't re-render the sort row.
  resetOrderForQueryChange() {
    const queryPresent = this.hasQueryTarget && this.queryTarget.value.trim() !== '';
    const defaultOrder = queryPresent ? 'most_relevant' : 'most_recent';

    this.element.querySelectorAll('input[name="order"]').forEach((radio) => {
      radio.checked = false;
      radio.closest('label')?.classList.toggle('is-default', radio.value === defaultOrder);
    });
  }

  toggleClearButton() {
    if (this.hasClearButtonTarget) {
      if (this.queryTarget.value.trim() === '') {
        this.clearButtonTarget.classList.add('hidden');
      } else {
        this.clearButtonTarget.classList.remove('hidden');
      }
    }
  }

  updateURL(event) {
    // Cold-modal live submits are count-only exploration; they must not replace
    // the page underneath the modal in browser history.
    // An aborted submit (Enter, then Clear all before the response) has no
    // fetchResponse — nothing to record, and reading it would throw.
    if (this.isCold) return;
    const url = event.detail?.fetchResponse?.response?.url;
    if (!url) return;
    // Inside an open mobile filter modal each submit is exploratory: replace the
    // history entry so cancel/back land on the committed URL, not a half-built
    // query (modal_filters_controller sets this flag while the modal is open).
    const method = this.element.dataset.modalSession ? 'replaceState' : 'pushState';
    window.history[method](window.history.state, null, url);
    window.rafaRememberVisit?.();
  }

  // A new search replaces the list in place. The page keeps its old scroll
  // offset, so someone who had moved down the previous results would land
  // mid-list on the new ones — including after closing the mobile modal,
  // which covers the list while the request runs. Snap to the top once the
  // results node has actually been swapped.
  scrollResultsToTop(event) {
    const stream = event.target;
    if (stream?.getAttribute?.('target') !== 'search_results') return;
    if (stream.getAttribute('action') !== 'update') return;

    const render = event.detail.render;
    event.detail.render = async (streamElement) => {
      await render(streamElement);
      this.clearPendingResults();
      window.scrollTo(0, 0);
    };
  }

  get modalController() {
    const modal = this.element.closest('[data-controller~="modal-filters"]');
    if (!modal) return null;

    // modal_filters_controller exposes itself on the element at connect time.
    // Prefer that stable link: Stimulus' application lookup can return null for
    // a parent scope while a child target callback is being connected.
    return modal.modal || this.application.getControllerForElementAndIdentifier(modal, 'modal-filters');
  }

  queryTargetConnected(target) {
    if (this.committedQuery === undefined) this.rememberQuery();

    // Typing only reveals the clear control. The search waits for Enter so a
    // mistype doesn't hit the reranker.
    target.addEventListener('input', () => {
      this.toggleClearButton();
    });

    // Enter/"Go" on the (mobile) keyboard signals the user is done typing: run
    // the search immediately and blur the input so the keyboard drops. Mobile
    // then leaves the filter sheet for the pending/results view; desktop keeps
    // its persistent sidebar. Without the blur, the keyboard obscures the state
    // transition and makes the submit feel unacknowledged.
    // preventDefault stops the native form submit that would double-fire here.
    target.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        target.blur();

        // In the cold/global modal there is no results region to update. Go
        // straight to /videos so an explicit search runs once, while the modal
        // becomes the transient results-loading shell. On the mobile results
        // page, Enter also means "show me results"; the old cards remain softly
        // visible until Turbo swaps them. Desktop keeps its persistent sidebar.
        if (this.isCold && this.modalController) {
          this.modalController.showResults(event);
        } else if (this.modalController?.isOpen) {
          this.modalController.submitQueryAndClose();
        } else {
          this.submitQuery();
        }
      }
    });
  }

  checkboxTargetConnected(target) {
    target.addEventListener('change', () => {
      this.submit();
    });
  }

  processFilterCountMutation(element) {
    const config = { childList: true, subtree: true, characterData: true };

    // Callback function to execute when mutations are observed
    const callback = (mutationList, observer) => {
      for (const mutation of mutationList) {
        if ( mutation.target.classList.contains('filter-count') ){
          const activeFilterCount = parseInt(mutation.target.innerHTML);
          if ( activeFilterCount > 0){
            mutation.target.classList.remove('hidden');
          } else {
            mutation.target.classList.add('hidden');
          }
        }
      }
    };

    // Create an observer instance linked to the callback function
    const observer = new MutationObserver(callback);

    // Start observing the target node for configured mutations
    observer.observe(element, config);

    // Later, you can stop observing
    // observer.disconnect();
  }
}
